/**
 * Pure, side-effect-free undo/redo history helpers for the builder's node
 * tree — LandingBuilder.jsx wires these into plain React state (setPast/
 * setFuture/setNodes) rather than owning the logic itself, so the history
 * mechanics stay directly unit-testable without mounting the component.
 */

export const MAX_HISTORY = 50;

/**
 * Appends `nodes` (the tree BEFORE the edit being recorded) onto `past`,
 * trimming from the front once maxHistory is exceeded.
 */
export function pushHistory({ past, nodes }, maxHistory = MAX_HISTORY) {
    const nextPast = [...past, nodes];
    return nextPast.length > maxHistory ? nextPast.slice(nextPast.length - maxHistory) : nextPast;
}

/**
 * Full state transition for a real edit: records the pre-edit tree onto
 * `past` and — as any new edit does — discards `future` (undoing back to a
 * point and then editing abandons the redo branch, standard editor
 * behavior).
 */
export function applyEdit({ past, nodes }, newNodes, maxHistory = MAX_HISTORY) {
    return {
        past: pushHistory({ past, nodes }, maxHistory),
        future: [],
        nodes: newNodes,
    };
}

/**
 * Steps one entry back. Returns null (no-op) when there is nothing to undo.
 */
export function undo({ past, future, nodes }) {
    if (past.length === 0) return null;
    const previous = past[past.length - 1];
    return {
        past: past.slice(0, -1),
        future: [nodes, ...future],
        nodes: previous,
    };
}

/**
 * Steps one entry forward. Returns null (no-op) when there is nothing to redo.
 */
export function redo({ past, future, nodes }) {
    if (future.length === 0) return null;
    const next = future[0];
    return {
        past: [...past, nodes],
        future: future.slice(1),
        nodes: next,
    };
}
